// src/utils/templateRegistry.js

/**
 * Single source of truth for “templateKey → publishedFormId”.
 * This lets all landing pages deep-link using templateKey
 * without hardcoding form IDs in multiple repos.
 *
 * Lifecycle note:
 * This registry is also the source of truth for template-level onboarding
 * metadata. Downstream onboarding logic should derive intent and v1 user state
 * from this metadata instead of hardcoding templateKey checks elsewhere.
 *
 * Important:
 * - Keep route/source metadata outside this file.
 * - Source values like "adobe_landing_nav" or "google_landing_nav" belong in
 *   onboardingLifecycle.js as explicit signup source mappings.
 * - This file should only describe templates and their lifecycle meaning.
 */
export const TEMPLATE_LIFECYCLE = Object.freeze({
  likelyIntent: Object.freeze({
    ADOBE: 'adobe',
    GOOGLE: 'google',
    UNKNOWN: 'unknown',
  }),

  templateFamily: Object.freeze({
    ADOBE_CID: 'adobeCid',
    ADOBE_UTM: 'adobeUtm',    
    GOOGLE_UA_UTM: 'googleUaUtm',
    GA4_UTM: 'ga4Utm',
  }),

  templateOutputMode: Object.freeze({
    TABLE: 'table',
    CARD: 'card',
  }),

  templateUseCase: Object.freeze({
    COPY_READY_LINKS: 'copyReadyLinks',
    BULK_CAMPAIGN_LINKS: 'bulkCampaignLinks',
    UNKNOWN: 'unknown',
  }),

  defaultUserState: Object.freeze({
    TEMPLATE_FREE_BUILDER_SIGNUP: 'templateFreeBuilderSignup',
  }),

  defaultActivationStatus: Object.freeze({
    TEMPLATE_OPENED: 'templateOpened',
  }),
});

export const TEMPLATE_REGISTRY = {
  // Adobe CID tool (table template)
  // Keep this existing entry until we verify whether this publishedFormId
  // is still referenced elsewhere in the app or older landing routes.
  adobe_cid_table: {
    publishedFormId: '414e75eb-3801-4387-be72-b97db9fd12d0',
    label: 'Adobe CID Builder (table)',
    lifecycle: {
      likelyIntent: TEMPLATE_LIFECYCLE.likelyIntent.ADOBE,
      templateFamily: TEMPLATE_LIFECYCLE.templateFamily.ADOBE_CID,
      templateOutputMode: TEMPLATE_LIFECYCLE.templateOutputMode.TABLE,
      templateUseCase: TEMPLATE_LIFECYCLE.templateUseCase.UNKNOWN,
      defaultUserState:
        TEMPLATE_LIFECYCLE.defaultUserState.TEMPLATE_FREE_BUILDER_SIGNUP,
      defaultActivationStatus:
        TEMPLATE_LIFECYCLE.defaultActivationStatus.TEMPLATE_OPENED,
      conciergeCandidateDefault: true,
    },
  },

  // Adobe CID tool (card template)
  // Used by public Adobe builder personalization flows such as:
  // /signup?intent=duplicate_template&templateKey=adobe_cid_card&source=adobe_landing_nav
  adobe_cid_card: {
    publishedFormId: 'c74742c1-5030-45f3-8807-fff972b330a8',
    label: 'Adobe CID Builder (card)',
    lifecycle: {
      likelyIntent: TEMPLATE_LIFECYCLE.likelyIntent.ADOBE,
      templateFamily: TEMPLATE_LIFECYCLE.templateFamily.ADOBE_CID,
      templateOutputMode: TEMPLATE_LIFECYCLE.templateOutputMode.CARD,
      templateUseCase: TEMPLATE_LIFECYCLE.templateUseCase.COPY_READY_LINKS,
      defaultUserState:
        TEMPLATE_LIFECYCLE.defaultUserState.TEMPLATE_FREE_BUILDER_SIGNUP,
      defaultActivationStatus:
        TEMPLATE_LIFECYCLE.defaultActivationStatus.TEMPLATE_OPENED,
      conciergeCandidateDefault: true,
    },
  },

  // Adobe CCID tool (card template)
  // Used by adobe-utm-builder.com personalization flows such as:
  // /signup?intent=duplicate_template&templateKey=adobe_ccid_card&source=adobe_utm_builder_microsite_ccid_builder
  adobe_ccid_card: {
    publishedFormId: '2ef06ec4-9537-4cf0-ab95-221fcba16d84',
    label: 'Adobe CCID Builder (card)',
    lifecycle: {
      likelyIntent: TEMPLATE_LIFECYCLE.likelyIntent.ADOBE,
      templateFamily: TEMPLATE_LIFECYCLE.templateFamily.ADOBE_CID,
      templateOutputMode: TEMPLATE_LIFECYCLE.templateOutputMode.CARD,
      templateUseCase: TEMPLATE_LIFECYCLE.templateUseCase.COPY_READY_LINKS,
      defaultUserState:
        TEMPLATE_LIFECYCLE.defaultUserState.TEMPLATE_FREE_BUILDER_SIGNUP,
      defaultActivationStatus:
        TEMPLATE_LIFECYCLE.defaultActivationStatus.TEMPLATE_OPENED,
      conciergeCandidateDefault: true,
    },
  },


  // Adobe UTM & CID Builder public tool (microsite homepage)
  // Used by adobe-utm-builder.com personalization flows such as:
  // /signup?intent=duplicate_template&templateKey=adobe_utm_builder&source=adobe_utm_builder_microsite_home
  adobe_utm_builder: {
    publishedFormId: 'ffb5a3f5-2317-45f9-9f51-84e34891f05b',
    label: 'Adobe UTM Builder',
    lifecycle: {
      likelyIntent: TEMPLATE_LIFECYCLE.likelyIntent.ADOBE,
      templateFamily: TEMPLATE_LIFECYCLE.templateFamily.ADOBE_UTM,
      templateOutputMode: TEMPLATE_LIFECYCLE.templateOutputMode.CARD,
      templateUseCase: TEMPLATE_LIFECYCLE.templateUseCase.COPY_READY_LINKS,
      defaultUserState:
        TEMPLATE_LIFECYCLE.defaultUserState.TEMPLATE_FREE_BUILDER_SIGNUP,
      defaultActivationStatus:
        TEMPLATE_LIFECYCLE.defaultActivationStatus.TEMPLATE_OPENED,
      conciergeCandidateDefault: true,
    },
  },

  // Adobe CID tool (bulk campaign links)
  // Live website pages route to this public form for "Adobe CID Builder for bulk campaign links".
  adobe_cid_bulk_table: {
    publishedFormId: '6d2a57d4-e476-415f-a07b-fb1234d7e466',
    label: 'Adobe CID Builder for bulk campaign links',
    lifecycle: {
      likelyIntent: TEMPLATE_LIFECYCLE.likelyIntent.ADOBE,
      templateFamily: TEMPLATE_LIFECYCLE.templateFamily.ADOBE_CID,
      templateOutputMode: TEMPLATE_LIFECYCLE.templateOutputMode.TABLE,
      templateUseCase: TEMPLATE_LIFECYCLE.templateUseCase.BULK_CAMPAIGN_LINKS,
      defaultUserState:
        TEMPLATE_LIFECYCLE.defaultUserState.TEMPLATE_FREE_BUILDER_SIGNUP,
      defaultActivationStatus:
        TEMPLATE_LIFECYCLE.defaultActivationStatus.TEMPLATE_OPENED,
      conciergeCandidateDefault: true,
    },
  },

  // Google Universal Analytics UTM tool (table template)
  google_ua_utm_table: {
    publishedFormId: '6aaf1fc6-b773-4ff4-8b1f-4ff2ebfd4e88',
    label: 'Google UA UTM Builder (table)',
    lifecycle: {
      likelyIntent: TEMPLATE_LIFECYCLE.likelyIntent.GOOGLE,
      templateFamily: TEMPLATE_LIFECYCLE.templateFamily.GOOGLE_UA_UTM,
      templateOutputMode: TEMPLATE_LIFECYCLE.templateOutputMode.TABLE,
      templateUseCase: TEMPLATE_LIFECYCLE.templateUseCase.BULK_CAMPAIGN_LINKS,
      defaultUserState:
        TEMPLATE_LIFECYCLE.defaultUserState.TEMPLATE_FREE_BUILDER_SIGNUP,
      defaultActivationStatus:
        TEMPLATE_LIFECYCLE.defaultActivationStatus.TEMPLATE_OPENED,
      conciergeCandidateDefault: true,
    },
  },

  // Google Universal Analytics UTM tool (card template)
  // Used by public Google builder personalization flows such as:
  // /signup?intent=duplicate_template&templateKey=google_ua_utm_card&source=google_landing_nav
  google_ua_utm_card: {
    publishedFormId: '7c5e43aa-b7f3-4943-a815-0bc20c826c62',
    label: 'Google UA UTM Builder (card)',
    lifecycle: {
      likelyIntent: TEMPLATE_LIFECYCLE.likelyIntent.GOOGLE,
      templateFamily: TEMPLATE_LIFECYCLE.templateFamily.GOOGLE_UA_UTM,
      templateOutputMode: TEMPLATE_LIFECYCLE.templateOutputMode.CARD,
      templateUseCase: TEMPLATE_LIFECYCLE.templateUseCase.COPY_READY_LINKS,
      defaultUserState:
        TEMPLATE_LIFECYCLE.defaultUserState.TEMPLATE_FREE_BUILDER_SIGNUP,
      defaultActivationStatus:
        TEMPLATE_LIFECYCLE.defaultActivationStatus.TEMPLATE_OPENED,
      conciergeCandidateDefault: true,
    },
  },

  // GA4 UTM Builder for copy-ready links
  // Live website pages route to this public form.
  ga4_utm_card: {
    publishedFormId: 'f5c7e1e9-c289-4503-8333-7ebcae0eda36',
    label: 'GA4 UTM Builder for copy-ready links',
    lifecycle: {
      likelyIntent: TEMPLATE_LIFECYCLE.likelyIntent.GOOGLE,
      templateFamily: TEMPLATE_LIFECYCLE.templateFamily.GA4_UTM,
      templateOutputMode: TEMPLATE_LIFECYCLE.templateOutputMode.CARD,
      templateUseCase: TEMPLATE_LIFECYCLE.templateUseCase.COPY_READY_LINKS,
      defaultUserState:
        TEMPLATE_LIFECYCLE.defaultUserState.TEMPLATE_FREE_BUILDER_SIGNUP,
      defaultActivationStatus:
        TEMPLATE_LIFECYCLE.defaultActivationStatus.TEMPLATE_OPENED,
      conciergeCandidateDefault: true,
    },
  },

  // GA4 UTM Builder for bulk campaign links
  // Live website pages route to this public form.
  ga4_utm_table: {
    publishedFormId: 'a5d20150-2a03-4cad-8a5f-3eb51f6d9298',
    label: 'GA4 UTM Builder for bulk campaign links',
    lifecycle: {
      likelyIntent: TEMPLATE_LIFECYCLE.likelyIntent.GOOGLE,
      templateFamily: TEMPLATE_LIFECYCLE.templateFamily.GA4_UTM,
      templateOutputMode: TEMPLATE_LIFECYCLE.templateOutputMode.TABLE,
      templateUseCase: TEMPLATE_LIFECYCLE.templateUseCase.BULK_CAMPAIGN_LINKS,
      defaultUserState:
        TEMPLATE_LIFECYCLE.defaultUserState.TEMPLATE_FREE_BUILDER_SIGNUP,
      defaultActivationStatus:
        TEMPLATE_LIFECYCLE.defaultActivationStatus.TEMPLATE_OPENED,
      conciergeCandidateDefault: true,
    },
  },


  // Add more as you roll them out:
  // ga4_utm_table: {
  //   publishedFormId: '...',
  //   label: 'GA4 UTM Builder (table)',
  //   lifecycle: {
  //     likelyIntent: TEMPLATE_LIFECYCLE.likelyIntent.GOOGLE,
  //     templateFamily: TEMPLATE_LIFECYCLE.templateFamily.GA4_UTM,
  //     templateOutputMode: TEMPLATE_LIFECYCLE.templateOutputMode.TABLE,
  //     defaultUserState:
  //       TEMPLATE_LIFECYCLE.defaultUserState.TEMPLATE_FREE_BUILDER_SIGNUP,
  //     defaultActivationStatus:
  //       TEMPLATE_LIFECYCLE.defaultActivationStatus.TEMPLATE_OPENED,
  //     conciergeCandidateDefault: true,
  //   },
  // },
};